// src/hooks/useDoctorProfile.js
"use client";

import { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchDoctorInfoByDoctorId,
  upsertDoctorInfo,
  updateDoctorInfoByDoctorId,
} from "../../ProfileDoctor/redux/thunks/doctorProfileThunks";

import {
  selectDoctorInfo,
  selectDoctorInfoLoading,
  selectDoctorInfoError,
  selectDoctorProfileMutationLoading,
  selectDoctorProfileMutationError,
} from "../../ProfileDoctor/redux/selectors/doctorProfileSelectors";

const asArray = (v) => (Array.isArray(v) ? v : []);
const pickFirst = (arr) => (Array.isArray(arr) && arr.length ? arr[0] : null);

export default function useDoctorProfile(doctorId) {
  const dispatch = useDispatch();

  const doctorInfo = useSelector(selectDoctorInfo);
  const loading = useSelector(selectDoctorInfoLoading);
  const error = useSelector(selectDoctorInfoError);

  const saving = useSelector(selectDoctorProfileMutationLoading);
  const saveError = useSelector(selectDoctorProfileMutationError);

  useEffect(() => {
    if (!doctorId) return;
    dispatch(fetchDoctorInfoByDoctorId(doctorId));
  }, [doctorId, dispatch]);

  const reload = useCallback(() => {
    if (!doctorId) return;
    return dispatch(fetchDoctorInfoByDoctorId(doctorId));
  }, [doctorId, dispatch]);

  const certifications = useMemo(
    () => asArray(doctorInfo?.certifications),
    [doctorInfo?.certifications]
  );

  const clinics = useMemo(
    () => asArray(doctorInfo?.clinics),
    [doctorInfo?.clinics]
  );

  const languages = useMemo(
    () => asArray(doctorInfo?.languages),
    [doctorInfo?.languages]
  );

  const insurances = useMemo(
    () => asArray(doctorInfo?.insurances),
    [doctorInfo?.insurances]
  );

  const officeHours = useMemo(
    () => asArray(doctorInfo?.officeHours),
    [doctorInfo?.officeHours]
  );

  const clinic = useMemo(() => pickFirst(clinics), [clinics]);

  const saveDoctorInfo = useCallback(
    async (payload) => {
      if (!doctorId) throw new Error("No doctorId");

      const dto = {
        doctorId,
        certifications: payload?.certifications ?? [],
        clinics: payload?.clinics ?? [],
        languages: payload?.languages ?? [],
        insurances: payload?.insurances ?? [],
        officeHours: payload?.officeHours ?? [],
      };

      return dispatch(upsertDoctorInfo(dto)).unwrap();
    },
    [doctorId, dispatch]
  );

  // ✅ NOUVEAU: Sauvegarde forcée avec validation
  const forceSaveDoctorInfo = useCallback(
    async (payload) => {
      if (!doctorId) throw new Error("No doctorId");

      // Validation et normalisation des données
      const validatePayload = (p) => {
        const clinics = Array.isArray(p?.clinics)
          ? p.clinics
              .filter((clinic) => clinic && typeof clinic === "object")
              .map((clinic) => ({
                name: String(clinic.name || clinic.clinicName || "").trim(),
                address: {
                  street: String(clinic.address?.street || "").trim(),
                  city: String(clinic.address?.city || "").trim(),
                  country: String(clinic.address?.country || "").trim(),
                  zip: String(clinic.address?.zip || "").trim(),
                },
                officeHours: String(clinic.officeHours || "").trim(),
              }))
                .filter((clinic) => clinic.name)
              : [];

        const validated = {
          doctorId,
          certifications: Array.isArray(p?.certifications) 
            ? p.certifications.filter(c => c && Object.keys(c).length > 0) 
            : [],
          clinics,
          languages: Array.isArray(p?.languages)
            ? p.languages.filter(l => l && Object.keys(l).length > 0)
            : [],
          insurances: Array.isArray(p?.insurances)
            ? p.insurances.map((insurance) => String(insurance || "").trim()).filter(Boolean)
            : [],
          officeHours: Array.isArray(p?.officeHours) ? p.officeHours : [],
        };

        // Supprimer les clés undefined
        Object.keys(validated).forEach(
          key => validated[key] === undefined && delete validated[key]
        );

        return validated;
      };

      const validatedDto = validatePayload(payload);

      console.group("╔══ [useDoctorProfile] forceSaveDoctorInfo ══╗");
      console.log("Payload validé:", JSON.stringify(validatedDto, null, 2));
      console.groupEnd();

      // Upsert principal
      const result = await dispatch(upsertDoctorInfo(validatedDto)).unwrap();

      // Vérification post-sauvegarde avec rechargement
      await new Promise(resolve => setTimeout(resolve, 500)); // délai pour la DB
      let reloaded = null;
      try {
        reloaded = await dispatch(fetchDoctorInfoByDoctorId(doctorId)).unwrap();
      } catch (reloadError) {
        console.warn("Doctor info saved, but reload failed:", reloadError);
      }

      // Vérifier que les données clés ont bien été sauvegardées
      const verifyData = (original, saved) => {
        const issues = [];
        return issues;
      };

      const verificationIssues = verifyData(validatedDto, reloaded);
      if (verificationIssues.length > 0) {
        console.warn("⚠️ [useDoctorProfile] Vérification échouée:", verificationIssues);
        // On peut choisir de re-sauvegarder ou d'alerter
      }

      return result;
    },
    [doctorId, dispatch]
  );

  const patchDoctorInfo = useCallback(
    async (patch) => {
      if (!doctorId) throw new Error("No doctorId");
      return dispatch(updateDoctorInfoByDoctorId({ doctorId, patch })).unwrap();
    },
    [doctorId, dispatch]
  );

  return {
    doctorId,
    doctorInfo,
    certifications,
    clinics,
    clinic,
    languages,
    insurances,
    officeHours,
    loading,
    error,
    saving,
    saveError,
    reload,
    saveDoctorInfo,
    forceSaveDoctorInfo,
    patchDoctorInfo,
  };
}